<template>
  <div class="card-kpi">
    <!-- Card Header -->
    <div class="card-header">
      <div class="card-header-left">
        <h3 class="card-title">{{ title }}</h3>
        <span v-if="subtitle" class="card-subtitle">{{ subtitle }}</span>
      </div>
      <div class="card-header-right">
        <slot name="controls"></slot>
      </div>
    </div>

    <!-- Card Body -->
    <div class="card-body">
      <!-- Primary Value -->
      <div class="kpi-value">{{ formattedValue }}</div>

      <!-- Description (always right under the value) -->
      <div v-if="description && variant !== 'comparison'" class="kpi-description">{{ description }}</div>

      <!-- Secondary Indicator based on variant -->
      <div v-if="variant === 'comparison' && change !== null" class="kpi-change" :class="changeClass">
        <span class="change-icon">{{ changeIcon }}</span>
        <span class="change-value">{{ formattedChange }}</span>
        <span v-if="changePeriod" class="change-period">{{ changePeriod }}</span>
      </div>

      <div v-else-if="variant === 'goal' && goal !== null" class="kpi-goal">
        <div class="goal-bar">
          <div class="goal-fill" :style="{ width: goalPercent + '%' }">
            <span class="goal-percent">{{ goalPercent }}%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CardKpi',
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    value: {
      type: [Number, String],
      required: true
    },
    variant: {
      type: String,
      default: 'standard',
      validator: (v) => ['standard', 'comparison', 'goal'].includes(v)
    },
    format: {
      type: String,
      default: 'number',
      validator: (v) => ['number', 'currency', 'percent'].includes(v)
    },
    description: {
      type: String,
      default: ''
    },
    // Comparison variant props
    change: {
      type: Number,
      default: null
    },
    changePeriod: {
      type: String,
      default: ''
    },
    // Goal variant props
    goal: {
      type: Number,
      default: null
    },
    goalLabel: {
      type: String,
      default: ''
    },
    // Formatting options
    prefix: {
      type: String,
      default: ''
    },
    suffix: {
      type: String,
      default: ''
    }
  },
  computed: {
    formattedValue() {
      if (typeof this.value === 'string') {
        return this.prefix + this.value + this.suffix;
      }
      return this.prefix + this.formatNumber(this.value) + this.suffix;
    },
    formattedChange() {
      if (this.change === null) return '';
      const sign = this.change >= 0 ? '+' : '';
      return `${sign}${this.change.toFixed(1)}%`;
    },
    changeClass() {
      if (this.change === null) return '';
      if (this.change > 0) return 'change-positive';
      if (this.change < 0) return 'change-negative';
      return 'change-neutral';
    },
    changeIcon() {
      if (this.change === null) return '';
      if (this.change > 0) return '↑';
      if (this.change < 0) return '↓';
      return '→';
    },
    goalPercent() {
      if (this.goal === null || this.goal === 0) return 0;
      const numValue = typeof this.value === 'number' ? this.value : parseFloat(this.value) || 0;
      return Math.min(100, Math.round((numValue / this.goal) * 100));
    },
    formattedGoalLabel() {
      const current = this.formatNumber(typeof this.value === 'number' ? this.value : parseFloat(this.value) || 0);
      const target = this.formatNumber(this.goal);
      return `${current} of ${target} (${this.goalPercent}%)`;
    }
  },
  methods: {
    formatNumber(num) {
      switch (this.format) {
        case 'currency':
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(num);
        case 'percent':
          return new Intl.NumberFormat('en-US', {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
          }).format(num / 100);
        default:
          return new Intl.NumberFormat('en-US').format(num);
      }
    }
  }
}
</script>

<style scoped>
.card-kpi {
  background: #FFF;
  overflow: hidden;
  min-height: 160px;
  display: flex;
  flex-direction: column;
}

.card-header {
  padding: 16px;
  background-color: var(--content-header-bg);
  border-bottom: var(--content-header-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-header-left {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.card-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
}

.card-subtitle {
  font-size: 13px;
  font-weight: 400;
  color: #666;
}

.card-body {
  padding: 24px;
  text-align: left;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 200px;
}

.kpi-value {
  font-size: 36px;
  font-weight: 700;
  color: #262626;
  line-height: 1.2;
}

.kpi-description {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
}

/* Comparison variant */
.kpi-change {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 4px 10px;
  border-radius: var(--border-radius);
  font-size: 13px;
  font-weight: 500;
}

.change-positive {
  background-color: rgba(5, 150, 105, 0.1);
  color: #059669;
}

.change-negative {
  background-color: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}

.change-neutral {
  background-color: rgba(107, 114, 128, 0.1);
  color: #6b7280;
}

.change-icon {
  font-size: 12px;
}

.change-period {
  color: #9ca3af;
  font-weight: 400;
}

/* Goal variant */
.kpi-goal {
  margin-top: 12px;
}

.goal-progress {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.goal-bar {
  height: 24px;
  background: #e5e7eb;
  border-radius: var(--border-radius);
  overflow: hidden;
  position: relative;
}

.goal-fill {
  height: 100%;
  background: linear-gradient(90deg, #4338ca, #6366f1);
  border-radius: var(--border-radius);
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 40px;
}

.goal-percent {
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding-right: 8px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.goal-label {
  font-size: 13px;
  color: #666;
}
</style>
